/**
 * The History tab every record page shows — plan §4.2: "Every record page has a
 * History tab rendering the timeline from this table".
 *
 * One component for every record in the app, because the audit log has one
 * shape for every table. A field-level change renders as `old → new`; a
 * `@sensitive` column arrives as `{changed: true}` and renders as "changed"
 * with no values, because addendum §D never records those by value.
 */
import { useEffect, useState } from 'react'
import {
  NOTIFICATION_CHANNELS, NOTIFICATION_EVENT_LABELS, NOTIFICATION_EVENTS,
} from '@rowted/shared'
import { Timeline, type TimelineEntry } from './overlays.js'
import { EmptyState, humanize } from './primitives.js'
import { HistoryEmpty } from '../record/record-empties.js'
import { historyFieldLabel, historyValue, isReference } from './history-words.js'
import {
  formatDocumentDate, formatListDateTime, formatMoneyText, formatRelative,
} from '../lib/format.js'
import { request } from '../lib/api.js'

interface AuditRow {
  id: string
  table_name: string
  record_id: string
  action: string
  changed_at: string
  user_name: string | null
  impersonator_name: string | null
  source: string
  reason: string | null
  detail: string | null
  changes: Record<string, { old?: unknown; new?: unknown; changed?: true }>
}

const ACTION_VERBS: Record<string, string> = {
  insert: 'Created',
  update: 'Changed',
  delete: 'Removed',
  restore: 'Restored',
  post: 'Posted',
  reverse: 'Reversed',
}

/** Field names come from the database, so they are read as English here. */
const humanizeField = (field: string): string =>
  field.replace(/_/g, ' ').replace(/^./, (character) => character.toUpperCase())

/**
 * The notification grid, as a sentence.
 *
 * It is stored as jsonb, so the generic renderer could only call it "a new
 * value" — which is precisely no help on the tab that exists to answer "why
 * did Rita stop getting invoices?". null is the inheriting state, and says so.
 */
function renderGrid(value: unknown): string {
  if (value === null || value === undefined) return 'the contact\u2019s defaults'
  if (typeof value !== 'object') return String(value)

  const grid = value as Record<string, Record<string, boolean> | undefined>
  const on = NOTIFICATION_EVENTS
    .map((event) => {
      const channels = NOTIFICATION_CHANNELS.filter((channel) => grid[event]?.[channel])
      if (channels.length === 0) return null
      const how = channels.map((channel) => (channel === 'sms' ? 'text' : 'email')).join(' and ')
      return `${NOTIFICATION_EVENT_LABELS[event].toLowerCase()} by ${how}`
    })
    .filter(Boolean)

  return on.length > 0 ? on.join(', ') : 'nothing'
}

/**
 * A value as words — never a raw ISO stamp (walkthrough Oct 30, §8): a moment
 * is *Oct 30, 1:58 pm*, a day *Oct 30, 2026*, money *$1,792.00*, a key
 * *In progress*. Classified in `history-words.ts`, formatted here with the
 * app's own formatters.
 */
function renderValue(field: string, value: unknown): string {
  if (field === 'notification_prefs' || field === 'notification_defaults') {
    return renderGrid(value)
  }
  const shown = historyValue(field, value)
  switch (shown.kind) {
    case 'empty': return 'empty'
    case 'moment': return formatListDateTime(shown.iso)
    case 'day': return formatDocumentDate(shown.iso)
    case 'money': return formatMoneyText(shown.amount)
    case 'text': return shown.text
  }
}

/*
 * The status columns of a job, which its own `job_status_history` row tells —
 * once, with the event as its reason (walkthrough Oct 30, §8; job-stages.md
 * §7). Left out of the `jobs` row only when that row is on the tab too.
 */
const JOB_MOVE_COLUMNS = new Set(['status', 'status_reason', 'status_changed_at', 'status_changed_by'])
const NOTHING: ReadonlySet<string> = new Set()

/**
 * "Phone (717) 555-0142 → (717) 555-0190", or "Password changed" when
 * sensitive — in the record's own words, never the column's
 * (`history-words.ts`). A reference to another row (a salesperson, a crew)
 * has no words in the log, only an id, so it says it changed.
 */
function describeChanges(
  table: string, changes: AuditRow['changes'], leaveOut: ReadonlySet<string>,
): string | null {
  const said = new Set<string>()
  const lines: string[] = []
  for (const [field, change] of Object.entries(changes ?? {})) {
    if (leaveOut.has(field)) continue
    const label = historyFieldLabel(table, field)
    if (label === null) continue
    const words = label ?? humanizeField(field)
    if (said.has(words)) continue
    said.add(words)
    /* A grid is a sentence (`renderGrid`); any other object or list has no short words. */
    const grid = field === 'notification_prefs' || field === 'notification_defaults'
    const shapeless = (value: unknown) => !grid && typeof value === 'object' && value !== null
    const unsaid = change.changed
      || isReference(change.old) || isReference(change.new)
      || shapeless(change.old) || shapeless(change.new)
    lines.push(unsaid
      ? `${words} changed`
      : `${words}: ${renderValue(field, change.old)} → ${renderValue(field, change.new)}`)
  }
  if (lines.length === 0) return null
  return lines.slice(0, 4).join(' · ') + (lines.length > 4 ? ` · and ${lines.length - 4} more` : '')
}

/** A move of a job, from its own row: *Quoted → Approved*. */
function describeMove(changes: AuditRow['changes']): string {
  const move = changes?.status
  const from = typeof move?.old === 'string' ? humanize(move.old) : null
  const to = typeof move?.new === 'string' ? humanize(move.new) : ''
  return from ? `${from} → ${to}` : to
}

export interface HistoryTabProps {
  table: string
  recordId: string
  /**
   * Other rows that belong to this record's story — a contact's links to
   * properties, its portal access. A change to a link is what answers "why did
   * Rita stop getting invoices?", and it is not a row on `contacts`.
   */
  related?: { tables: string[]; recordIds: string[] }
  /**
   * Ask a route that works out the rows itself, instead of naming them.
   *
   * A **map**'s history is its own row plus every layer and every shape, so
   * `recordIds` would be hundreds of ids sent on every page open
   * (`property-maps-v2.md` §4.1). The route still answers the same shape, so
   * everything below this line is unchanged — only the question moves.
   */
  source?: string
  limit?: number
  /** Shown instead of the full timeline in a profile's Recent activity card. */
  compact?: boolean
}

export function HistoryTab({
  table, recordId, related, source, limit = 50, compact,
}: HistoryTabProps) {
  const [rows, setRows] = useState<AuditRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const relatedTables = related?.tables.join(',') ?? ''
  const relatedIds = related?.recordIds.join(',') ?? ''

  useEffect(() => {
    let cancelled = false
    const query = new URLSearchParams(source
      ? { limit: String(limit) }
      : {
        tables: [table, ...(relatedTables ? relatedTables.split(',') : [])].join(','),
        recordIds: [recordId, ...(relatedIds ? relatedIds.split(',') : [])].join(','),
        limit: String(limit),
      })

    request<{ items: AuditRow[] }>(`${source ?? '/admin/history'}?${query.toString()}`)
      .then((result) => { if (!cancelled) setRows(result.items) })
      .catch((cause: unknown) => {
        if (cancelled) return
        // A viewer without audit_log access is not an error state — the tab
        // simply has nothing it may show them (addendum §D.5).
        setError(cause instanceof Error ? cause.message : 'History could not load.')
      })

    return () => { cancelled = true }
  }, [table, recordId, relatedTables, relatedIds, source, limit])

  if (error) {
    return <EmptyState title="History is not visible to you" body={error} />
  }
  if (!rows) {
    return <p className="m-0 p-4 text-cell text-text-muted">Loading…</p>
  }
  /*
   * **History is never truly empty** (§8): the audit insert row is always its
   * first line, so the ordinary new record is a timeline of one. What is
   * missing on it is not rows — it is the explanation of why this one tab has
   * no button, which `HistoryEmpty` carries under the line.
   *
   * No rows at all means a record that predates the log or came in through an
   * import. Those are real, and an empty tab with no words on it reads as
   * broken rather than as old.
   */
  if (rows.length === 0) {
    return <HistoryEmpty created={false} />
  }

  /*
   * A job's move is one line — its `job_status_history` row — so the `jobs`
   * update that made it leaves its status columns out, and goes altogether
   * when they were all it changed (walkthrough Oct 30, §8).
   */
  const movesShown = related?.tables.includes('job_status_history') ?? false
  const leaveOutOf = (row: AuditRow): ReadonlySet<string> =>
    (movesShown && row.table_name === 'jobs' ? JOB_MOVE_COLUMNS : NOTHING)

  const entries: TimelineEntry[] = rows.flatMap((row) => {
    const move = row.action === 'status' && row.table_name === 'job_status_history'
    // On an insert every column is "changed", which says nothing. The fact of
    // creation is the entry; the values are the record itself.
    const changed = row.action === 'insert' || move
      ? null
      : describeChanges(row.table_name, row.changes, leaveOutOf(row))
    if (row.action === 'update' && !changed && !row.detail && movesShown
      && row.table_name === 'jobs' && Object.keys(row.changes ?? {}).some((field) => JOB_MOVE_COLUMNS.has(field))) {
      return []
    }
    return [{
      id: row.id,
      at: formatRelative(row.changed_at),
      who: row.impersonator_name
        // Addendum §4.1: an impersonated action names both people, always.
        ? `${row.user_name ?? 'Someone'} (as ${row.impersonator_name})`
        : row.user_name ?? sourceName(row.source),
      what: move
        ? describeMove(row.changes)
        : row.detail
          ?? `${ACTION_VERBS[row.action] ?? row.action} ${subjectOf(row.table_name, table)}`,
      detail: [changed, row.reason].filter(Boolean).join(' — ') || undefined,
    }]
  })
  if (entries.length === 0) return <HistoryEmpty created={false} />

  return (
    <div className={compact ? '' : 'p-1'}>
      <Timeline entries={compact ? entries.slice(0, 6) : entries} />
      {/*
        * Only while there is one line — a created row and nothing else.
        * Past that the tab explains itself, and a paragraph about what gets
        * written here under forty rows of it is a paragraph nobody reads.
        * Never in `compact`, which is a card on another screen.
        */}
      {!compact && entries.length === 1 && <HistoryEmpty created />}
    </div>
  )
}

/** "this record", or what the related row actually is. */
function subjectOf(tableName: string, ownTable: string): string {
  if (tableName === ownTable) return 'this record'
  switch (tableName) {
    case 'contact_property_links': return 'who is told what, on a property'
    case 'portal_access': return 'portal access'
    case 'contacts': return 'a contact on this property'
    /* The visit page's days, the invoice's lines, a machine's work orders. */
    case 'visits': case 'tickets': return 'a day of this visit'
    case 'invoice_lines': return 'a line'
    case 'work_orders': return 'a work order'
    default: return tableName.replace(/_/g, ' ')
  }
}

/** An unattributed row was written by the system, not by nobody. */
function sourceName(source: string): string {
  switch (source) {
    case 'system': return 'Rowted'
    case 'integration': return 'An integration'
    case 'ai': return 'Assistant'
    case 'portal': return 'The customer'
    default: return 'Someone'
  }
}
